import { Transition } from "@headlessui/react";
import { useRef, useState } from "react";
import { Image as DatoImage, renderMetaTags, TitleMetaLinkTag, useQuerySubscription } from "react-datocms";
import { GetStaticProps, GetStaticPaths } from "next";
import { Api } from "@/api";
import {
  Layout,
  SEO,
  Section,
  Container,
  BlogAuthor,
  PaidContentForm,
  BloggerForm,
  AmbassadorForm,
  InstagramForm,
  WorkshopsForm,
  SuccessMessage,
  RightArrowIcon,
} from "@/components";
import { IS_DEVELOPMENT, ROUTES, DATOCMS_API_TOKEN } from "@/config";
import { publishEvent, TOPICS } from "@/events";
import { metaTagsFragment, responsiveImageFragment, structuredTextFields, StructuredText } from "@/modules";
import { useAlertDispatch } from "@/providers";
import { Subscription, IPartnership, PartnershipSubmitHandlerParams } from "@/types";
import { mockApiRequest } from "@/utils";
import { HorizontalRule, AlertActionType } from "@narrative-software/narrative-web-ui";

const ArrowLink: React.FC<{ href: string }> = ({ href, children }) => (
  <a href={href} className="group relative block py-2 pr-15 mt-6 border-b border-black">
    {children}
    <RightArrowIcon className="absolute bottom-0 right-0 mb-3 transition-transform duration-200 transform -translate-x-3 group-hover:translate-x-0" />
  </a>
);

export const PARTNERSHIP_FORM_MAP = {
  "paid-content": PaidContentForm,
  instagram: InstagramForm,
  blogger: BloggerForm,
  ambassador: AmbassadorForm,
  workshops: WorkshopsForm,
};

interface IPartnershipPage {
  otherPages: IPartnership[];
  page: IPartnership & {
    seo: TitleMetaLinkTag[];
  };
}

type Props = {
  preview: boolean;
  subscription: Subscription<IPartnershipPage>;
};

const PartnershipPage: React.FC<Props> = ({ preview, subscription }) => {
  const alertDispatch = useAlertDispatch();
  const [height, setHeight] = useState(0);
  const [isSuccess, setSuccess] = useState(false);
  const formContainer = useRef<HTMLDivElement>(null);

  const { data } = useQuerySubscription(subscription);
  if (!data) return null;

  const {
    otherPages,
    page: { seo, title, date, form, image, author, content },
  } = data;

  // Form
  const Form = PARTNERSHIP_FORM_MAP[form];

  // Handle submit
  const handleSubmit = async <T extends Record<string, string>>({
    values,
    actions,
    fieldMap,
    hiddenInputs,
  }: PartnershipSubmitHandlerParams<T>) => {
    try {
      // Set form container height for a smoother fade in of the success message
      if (formContainer.current) {
        setHeight(formContainer.current.clientHeight);
      }

      // Build form value object
      const data = Object.entries(values).reduce(
        (accumulator, [key, value]) => ({
          ...accumulator,
          [fieldMap[key]]: value,
        }),
        { ...hiddenInputs },
      );

      const response = IS_DEVELOPMENT ? await mockApiRequest() : await Api.sendActiveCampaignsForm(data);

      if (response?.status >= 200 && response?.status < 300) {
        setSuccess(true);
      } else {
        throw new Error();
      }
    } catch (error) {
      setSuccess(false);
      alertDispatch({
        type: AlertActionType.SetContent,
        payload: {
          type: "error",
          title: "There was an error submitting your form. Please try again.",
        },
      });

      /**
       * Send an event to track form failures.
       * We're sending the form type, error message and the values entered by the user
       */
      publishEvent(TOPICS.partnershipFormFailed, {
        form,
        error,
        values,
      });
    } finally {
      // Dirty timeout to prevent the success message transition being affected by resetting the form state
      setTimeout(() => {
        actions.setSubmitting(false);
      }, 1000);
    }
  };

  return (
    <Layout preview={preview}>
      <SEO title={`${title} | Narrative`}>{renderMetaTags(seo)}</SEO>

      <Section isFirstSection>
        <Container className="flex flex-col items-center">
          <div className="max-w-4xl">
            <h1 className="text-h2 font-semibold text-center leading-tight lg:text-h1">{title}</h1>
            <HorizontalRule topMargin />
          </div>

          {image && (
            <div className="mt-10 border border-current">
              <DatoImage data={image.responsiveImage} />
            </div>
          )}

          <div className="w-full max-w-200">
            {author && (
              <div className="my-8">
                <BlogAuthor name={author.name} date={date} image={author.avatar} />
              </div>
            )}
            <div className="prose text-stealth-bomber">
              <StructuredText data={content} />
            </div>
          </div>
        </Container>
      </Section>

      <Section className="bg-white">
        <Container className="flex flex-col items-center">
          <div
            ref={formContainer}
            className="w-full max-w-200 overflow-hidden"
            style={{ maxHeight: height ? `${height}px` : "none" }}
          >
            <Transition
              show={!isSuccess}
              leave="transition-opacity duration-500"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <HorizontalRule bottomMargin />
              <Form onSubmit={handleSubmit} />
            </Transition>

            <Transition
              show={isSuccess}
              enter="transition-opacity duration-500 delay-500"
              enterFrom="opacity-0"
              enterTo="opacity-100"
            >
              <HorizontalRule bottomMargin />
              <SuccessMessage />
            </Transition>
          </div>
        </Container>
      </Section>

      {otherPages.length ? (
        <Section>
          <Container className="flex flex-col items-center">
            <div className="w-full max-w-200">
              <HorizontalRule bottomMargin />
              <h2 className="text-h3 font-semibold leading-compact text-center lg:text-h2">Other ways to partner up</h2>
              <div className="mt-12">
                {otherPages.map(({ id, title, slug, parentPage }) => (
                  <ArrowLink key={id} href={`/${parentPage.slug}/${slug}`}>
                    {title}
                  </ArrowLink>
                ))}
                <ArrowLink href={`/${ROUTES.PARTNERSHIPS.SLUG}`}>Partnerships page</ArrowLink>
              </div>
            </div>
          </Container>
        </Section>
      ) : null}
    </Layout>
  );
};

/**
 * Static paths
 */
export const getStaticPaths: GetStaticPaths = async () => {
  const {
    data: {
      data: { pages },
    },
  } = await Api.datocmsGraphqlRequest<{ pages: IPartnership[] }>({
    query: `
      {
        pages: allPagePartnerships {
          slug
        }
      }
    `,
  });

  return {
    paths: pages.map(({ slug }) => ({ params: { slug } })),
    fallback: false,
  };
};

/**
 * Static props
 */
export const getStaticProps: GetStaticProps<Props> = async ({ params, preview = false }) => {
  const graphqlRequest = {
    preview,
    variables: {
      slug: params?.slug,
    },
    query: `
      query($slug: String) {
        page: pagePartnership(filter: { slug: { eq: $slug }}) {
          id
          title
          slug
          date
          form
          content {
            ${structuredTextFields}
          }
          author {
            name
            avatar {
              responsiveImage(imgixParams: { auto: format, fit: crop, w: 144, h: 144 }) {
                ...responsiveImageFragment
              }
            }
          }
          image {
            responsiveImage(imgixParams: { auto: format, fit: crop, w: 2428, h: 1360 }) {
              ...responsiveImageFragment
            }
          }
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
        }

        otherPages: allPagePartnerships(
          orderBy: position_ASC,
          filter: { slug: { neq: $slug }}
        ) {
          id
          title
          slug
          parentPage {
            slug
          }
        }
      }
      ${metaTagsFragment}
      ${responsiveImageFragment}
    `,
  };

  const {
    data: { data: initialData },
  } = await Api.datocmsGraphqlRequest<IPartnershipPage>(graphqlRequest);

  // If the page doesn’t exist, throw 404
  if (!initialData?.page) {
    return { notFound: true };
  }

  return {
    props: {
      preview,
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData,
            token: DATOCMS_API_TOKEN,
          }
        : {
            initialData,
            enabled: false,
          },
    },
  };
};

export default PartnershipPage;
